import { type Zeitraum } from '../../dtos';
import { content } from '../content';
import { GERMAN_LOCALE, GERMAN_TIMEZONE } from './german';

const dateFormatter = new Intl.DateTimeFormat(GERMAN_LOCALE, {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: GERMAN_TIMEZONE,
});
const dateFormatterWithoutWeekday = new Intl.DateTimeFormat(GERMAN_LOCALE, {
  dateStyle: 'medium',
  timeZone: GERMAN_TIMEZONE,
});
const timeFormatter = new Intl.DateTimeFormat(GERMAN_LOCALE, { timeStyle: 'short', timeZone: GERMAN_TIMEZONE });
const timeFormatterMedium = new Intl.DateTimeFormat(GERMAN_LOCALE, { timeStyle: 'medium', timeZone: GERMAN_TIMEZONE });
const monthFormatter = new Intl.DateTimeFormat(GERMAN_LOCALE, { month: 'long', timeZone: GERMAN_TIMEZONE });

/*
 * Singles
 */

// November
const formatMonth = (date: Date) => monthFormatter.format(date);

// Januar 2024 - Januar 2025
// or
// Januar - November 2024
export const formatTimeSpanMonths = (start: Date, end: Date) => {
  const startMonth = formatMonth(start);
  const endMonth = formatMonth(end);

  if (start.getFullYear() === end.getFullYear()) {
    return `${startMonth} - ${endMonth} ${end.getFullYear()}`;
  }

  return `${startMonth} ${start.getFullYear()} - ${endMonth} ${end.getFullYear()}`;
};

// Di., 22.08.2024 or without weekday: 22.08.2024
export const formatDate = (date?: Date | null, withoutWeekday?: boolean) => {
  if (date) {
    if (withoutWeekday) {
      return dateFormatterWithoutWeekday.format(date);
    }

    return dateFormatter.format(date);
  }

  return '-';
};

// 09:00
export const formatTime = (date: Date) => timeFormatter.format(date);

// 09:00:00
const formatTimeWithSeconds = (date: Date) => timeFormatterMedium.format(date);

// 22.08.2024, 09:00:00
export const formatTimestamp = (date: Date) => `${formatDate(date)}, ${formatTimeWithSeconds(date)}`;

/*
 * Ranges
 */

// Fr., 22.08.2024, 09:00 - 17:00
// or without weekday: 22.08.2024, 09:00 - 17:00
export const formatTimeRangeForDate = (start: Date, end: Date, withoutWeekday?: boolean) =>
  `${formatDate(start, withoutWeekday)}, ${formatTime(start)} - ${formatTime(end)}`;

// 22.08.2024 - 31.03.2026
export const formatZeitraumDate = (zeitraum: Zeitraum, withoutWeekday?: boolean) =>
  formatDateRange(zeitraum.start, zeitraum.end, withoutWeekday);

// 22.08.2024 - 31.03.2026
export const formatDateRange = (start: Date, end: Date | null, withoutWeekday?: boolean) => {
  const formattedStart = formatDate(start, withoutWeekday);
  const formattedEnd = formatDate(end, withoutWeekday);

  if (formattedStart === formattedEnd) {
    return formattedStart;
  }

  return `${formattedStart} – ${end === null ? '∞' : formattedEnd}`;
};

// 22.08.2024 - 31.03.2026
// can be used to format a multi range, usually of an ablauf
export const formatDateMultiRange = (multirange: Zeitraum[], withoutWeekday?: boolean) => {
  if (multirange.length === 0) return content.noInfoAvailable;

  const start = multirange
    .map((ablaufTag) => ablaufTag.start)
    .reduce((previous, current) => (current < previous ? current : previous));
  const end = multirange
    .map((ablaufTag) => ablaufTag.end)
    .reduce((previous, current) => (current > previous ? current : previous));

  return formatDateRange(start, end, withoutWeekday);
};
